import { renderQuoteToPdf } from './renderQuoteToPdf';
import { buildPdfFilename, triggerPdfDownload } from './generatePdf';
import { copyShareTextToClipboard } from '@/lib/clipboardCopy';
import type { QuoteState } from '../types';

/**
 * Block 12 PR-D-B Phase G4 — Telegram desktop без URL preview.
 *
 * **G3 → G4 changelog.** G3 использовал `t.me/share/url?url=<origin>&text=...`
 * для Telegram — Aibek visual verify preview показал что Telegram отрисовывает
 * URL preview card нашего origin в compose сообщения (annoying UX). Попытки
 * убрать preview через empty `?url=&text=...` или omit `?url=` обе возвращают
 * **302 redirect** к telegram.org (Telegram server требует non-empty url для
 * share endpoint).
 *
 * G4 решение: Telegram desktop → `https://web.telegram.org/` через
 * `window.open(_blank)` (как MAX). Pre-fill через URL невозможен, но текст
 * КП в clipboard (B-141) → Ctrl+V в выбранный чат. Toast обновлён под этот
 * flow. Other channels (WhatsApp / MAX / Gmail / Mail / mobile) — UNCHANGED.
 *
 * **Pop-up blocker safety (G2/G3 invariant preserved).** `window.open` для всех
 * 4 desktop URL-каналов (whatsapp / telegram / max / gmail) вызывается
 * СИНХРОННО в start desktop branch — ДО `await renderQuoteToPdf` (200-
 * 800ms). Mail (mailto:) тоже sync через `window.location.href` (protocol
 * handler).
 *
 * **Mobile path (`channel === 'auto'`) — UNCHANGED от G1/G2/G3**: единый
 * `navigator.share` + fallback download + B-141 clipboard.
 *
 * **`mail-opened` status удалён в G2.** Desktop НЕ ИСПОЛЬЗУЕТ navigator.share
 * → fallback не нужен, единый `desktop-direct` status для всех 5 каналов.
 *
 * **`getOrigin` option зарезервирован.** G3 ввёл `getOrigin?: () => string`
 * для Telegram URL params; в G4 Telegram больше не нуждается в origin, но
 * опцию оставили в `ShareQuoteOptions` для future use (если другой канал
 * потребует знать origin приложения).
 */

export type ShareChannel = 'auto' | 'whatsapp' | 'telegram' | 'max' | 'mail' | 'gmail';

export type DesktopShareChannel = Exclude<ShareChannel, 'auto'>;

export type ShareResult =
  | { status: 'shared'; method: 'native' }
  | { status: 'cancelled' }
  | { status: 'downloaded'; method: 'fallback' }
  | { status: 'desktop-direct'; channel: DesktopShareChannel }
  | { status: 'error'; error: unknown };

export interface ShareQuoteOptions {
  quoteName?: string;
  /** Канал расшаривания. `auto` → mobile native sheet. Default `auto`. */
  channel?: ShareChannel;
  /** DI seam для тестов — defaults to global `navigator`. */
  nav?: Pick<Navigator, 'canShare' | 'share' | 'clipboard'>;
  /** Override download trigger — defaults to `triggerPdfDownload`. */
  download?: (blob: Blob, filename: string) => void;
  /**
   * Override для открытия URL (whatsapp / telegram / max / gmail / mailto).
   * `target='_self'` → `window.location.href = url` (protocol handler).
   * `target='_blank'` → `window.open(url, '_blank')`.
   * Defaults to wrapper над `window` если доступен.
   */
  openUrl?: (url: string, target: '_self' | '_blank') => void;
  /**
   * G3 ввёл этот seam для Telegram `?url=` query param; G4 переключил
   * Telegram на `web.telegram.org` (без origin). Опция зарезервирована
   * для future use (если другой канал потребует origin приложения).
   */
  getOrigin?: () => string;
}

export function buildShareText(state: QuoteState): string {
  const fioRow = state.executorContacts.find((c) => c.id === 'fio');
  const fio = fioRow?.value.trim() ?? '';
  const from = fio.length > 0 ? `от ${fio}` : 'от мебельщика';
  return `Коммерческое предложение ${from}. Файл в приложении.`;
}

/**
 * Toast text per desktop channel — UI слой импортирует и показывает
 * (parent `BottomActionButtons.tsx` после `onResult({status:'desktop-direct'})`).
 * Логика colocated с channel semantics, не размазана по UI.
 */
export function buildDesktopShareToast(channel: DesktopShareChannel): string {
  switch (channel) {
    case 'whatsapp':
      return 'WhatsApp открыт в новой вкладке. Прикрепите PDF из Downloads, текст уже в сообщении.';
    case 'telegram':
      return 'Telegram открыт в новой вкладке. Выберите чат, прикрепите PDF из Downloads, вставьте текст (Ctrl+V).';
    case 'max':
      return 'MAX открыт в новой вкладке. Откройте чат, прикрепите PDF из Downloads, вставьте текст (Ctrl+V).';
    case 'mail':
      return 'Откройте почтовый клиент и прикрепите PDF из Downloads.';
    case 'gmail':
      return 'Gmail открыт в новой вкладке. Прикрепите PDF из Downloads.';
  }
}

/** AbortError / NotAllowedError → user closed native sheet — silent UX. */
function isCancelledError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.name === 'AbortError' || err.name === 'NotAllowedError';
}

function defaultOpenUrl(url: string, target: '_self' | '_blank'): void {
  if (typeof window === 'undefined') return;
  if (target === '_self') {
    window.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

function buildWhatsAppUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/**
 * Web client Telegram. **G4:** G3 использовал `t.me/share/url?url=<origin>&text=...`
 * — Aibek visual verify preview показал что Telegram отрисовывает URL preview
 * card нашего origin в compose сообщения (annoying UX). Попытки убрать preview
 * через empty `?url=&text=...` или omit `?url=` обе возвращают 302 redirect
 * (Telegram server требует non-empty url для share endpoint). Решение —
 * открывать `web.telegram.org` без pre-fill. Текст КП по-прежнему в clipboard
 * (B-141) для paste в чат после выбора получателя.
 */
const TELEGRAM_WEB_URL = 'https://web.telegram.org/';

/** Web client MAX — pre-fill через URL невозможен (документированного API нет). */
const MAX_WEB_URL = 'https://web.max.ru/';

function buildMailtoUrl(title: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
}

function buildGmailComposeUrl(title: string, body: string): string {
  return `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
}

interface ShareCanShareNav {
  canShare?: Navigator['canShare'];
  share?: Navigator['share'];
}

/**
 * `canShare({ files: [...] })` может throw TypeError на iOS Safari 14
 * WebKit билдах вместо return false. Wrap в try/catch.
 */
function canShareFiles(nav: ShareCanShareNav | undefined, data: ShareData): boolean {
  if (!nav || typeof nav.canShare !== 'function' || typeof nav.share !== 'function') {
    return false;
  }
  try {
    return nav.canShare(data);
  } catch {
    return false;
  }
}

export async function shareQuotePdf(
  state: QuoteState,
  options: ShareQuoteOptions = {},
): Promise<ShareResult> {
  const {
    quoteName,
    channel = 'auto',
    download = triggerPdfDownload,
    openUrl = defaultOpenUrl,
  } = options;
  const nav = options.nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);

  try {
    // shareText / title — sync, не зависят от PDF render. Готовим заранее,
    // чтобы вызвать openUrl ДО async renderQuoteToPdf (popup blocker safety).
    const shareText = buildShareText(state);
    const title = 'Коммерческое предложение';

    if (channel !== 'auto') {
      // ===== Desktop direct URL path — skip navigator.share entirely =====
      //
      // 1. Sync openUrl FIRST (preserves transient userActivation для popup).
      //    Все 5 desktop каналов открывают URL до async PDF render (G3).
      if (channel === 'whatsapp') {
        openUrl(buildWhatsAppUrl(shareText), '_blank');
      } else if (channel === 'telegram') {
        openUrl(TELEGRAM_WEB_URL, '_blank');
      } else if (channel === 'max') {
        openUrl(MAX_WEB_URL, '_blank');
      } else if (channel === 'gmail') {
        openUrl(buildGmailComposeUrl(title, shareText), '_blank');
      } else if (channel === 'mail') {
        openUrl(buildMailtoUrl(title, shareText), '_self');
      }

      // 2. Async render PDF + auto-download (на ВСЕХ 5 каналах).
      const blob = await renderQuoteToPdf(state, quoteName);
      const filename = buildPdfFilename(quoteName, state.furnitureType);
      download(blob, filename);

      // 3. B-141 universal clipboard copy (на ВСЕХ 5 desktop каналах).
      //    Non-fatal на failure (insecure context / permission denial).
      const clipboardForCopy = nav?.clipboard as Pick<Clipboard, 'writeText'> | undefined;
      await copyShareTextToClipboard(shareText, { clipboard: clipboardForCopy });

      return { status: 'desktop-direct', channel };
    }

    // ===== Mobile native path (channel === 'auto') — UNCHANGED от G1 =====
    const blob = await renderQuoteToPdf(state, quoteName);
    const filename = buildPdfFilename(quoteName, state.furnitureType);
    const file = new File([blob], filename, { type: 'application/pdf' });
    const shareData: ShareData = { files: [file], title, text: shareText };

    // B-141 universal clipboard copy (mobile path — выполняется ДО share).
    const clipboardForCopy = nav?.clipboard as Pick<Clipboard, 'writeText'> | undefined;
    await copyShareTextToClipboard(shareText, { clipboard: clipboardForCopy });

    const canShare = canShareFiles(nav, shareData);

    if (canShare && nav !== undefined) {
      try {
        await nav.share!(shareData);
        return { status: 'shared', method: 'native' };
      } catch (err) {
        if (isCancelledError(err)) return { status: 'cancelled' };
        return { status: 'error', error: err };
      }
    }

    download(blob, filename);
    return { status: 'downloaded', method: 'fallback' };
  } catch (err) {
    return { status: 'error', error: err };
  }
}
